import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, CheckCircle2, XCircle, Clock, Loader2, ShieldCheck,
    GitBranch, Users, Zap, StopCircle, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { readSseLines } from "@/lib/sse";

/* ================================================================
   Nexus One · OrchestrationRunDetail — SSE-driven run monitor
   Left column: node cards with per-node status.
   Right column: chronological timeline (input/output).
   Approval gates render inline Approve/Reject action.
   ================================================================ */

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NODE_META = {
    trigger:          { color: "#8b5cf6", icon: Zap,         label: "Trigger" },
    workforce_action: { color: "#22d3ee", icon: Users,       label: "Workforce Action" },
    condition:        { color: "#fbbf24", icon: GitBranch,   label: "Condition" },
    approval_gate:    { color: "#f472b6", icon: ShieldCheck, label: "Approval Gate" },
    end:              { color: "#34d399", icon: StopCircle,  label: "End" },
};

const STATUS_STYLE = {
    running:            { color: "#8b5cf6", bg: "rgba(139,92,246,0.14)", border: "rgba(139,92,246,0.55)" },
    awaiting_approval:  { color: "#fbbf24", bg: "rgba(245,158,11,0.14)", border: "rgba(245,158,11,0.55)" },
    success:            { color: "#34d399", bg: "rgba(52,211,153,0.14)", border: "rgba(52,211,153,0.55)" },
    failed:             { color: "#f87171", bg: "rgba(239,68,68,0.14)", border: "rgba(239,68,68,0.45)" },
    cancelled:          { color: "#94a3b8", bg: "rgba(148,163,184,0.14)", border: "rgba(148,163,184,0.45)" },
    pending:            { color: "#94a3b8", bg: "rgba(148,163,184,0.14)", border: "rgba(148,163,184,0.45)" },
};

export default function OrchestrationRunDetail() {
    const { orchId, runId } = useParams();
    const nav = useNavigate();
    const [run, setRun] = useState(null);
    const [orch, setOrch] = useState(null);
    const [loading, setLoading] = useState(true);
    const [decisionBusy, setDecisionBusy] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const activeStream = useRef(false);

    const fetchOnce = async () => {
        try {
            const [rR, oR] = await Promise.all([
                api.get(`/orchestration-runs/${runId}`),
                api.get(`/orchestrations/${orchId}`),
            ]);
            setRun(rR.data);
            setOrch(oR.data);
        } catch (e) { toast.error("Could not load run."); }
        finally { setLoading(false); }
    };

    // Initial fetch + SSE stream
    useEffect(() => {
        (async () => {
            await fetchOnce();
            await startStream();
        })();
        return () => { activeStream.current = false; };
    // eslint-disable-next-line
    }, [runId]);

    const startStream = async () => {
        if (activeStream.current) return;
        activeStream.current = true;
        try {
            const token = localStorage.getItem("wfos_token");
            const resp = await fetch(`${API_BASE}/orchestration-runs/${runId}/stream`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) throw new Error("SSE open failed");
            for await (const ev of readSseLines(resp)) {
                if (!activeStream.current) break;
                if (ev.type === "snapshot" && ev.run) setRun(ev.run);
                if (ev.type === "done") break;
                if (ev.type === "error") toast.error(ev.message || "Stream error.");
            }
        } catch (e) {
            // Fallback: polling
            let tries = 0;
            while (activeStream.current && tries < 40) {
                await new Promise((r) => setTimeout(r, 1500));
                await fetchOnce();
                tries += 1;
                if (run && ["success", "failed", "cancelled", "awaiting_approval"].includes(run.status)) break;
            }
        } finally {
            activeStream.current = false;
        }
    };

    const approveGate = async (decision) => {
        if (!run || !run.awaiting_approval_for) return;
        if (decision === "rejected" && !rejectReason.trim()) {
            toast.error("Rejection requires a reason.");
            return;
        }
        setDecisionBusy(true);
        try {
            const { data } = await api.post(`/orchestration-runs/${runId}/approve`, {
                node_id: run.awaiting_approval_for,
                decision,
                reason: decision === "rejected" ? rejectReason : "Approved via Nexus One console.",
            });
            setRun(data.run);
            toast.success(decision === "approved" ? "Approved — resuming run." : "Rejected — run marked failed.");
            setRejectReason("");
            // Resume streaming after approval since backend will keep executing
            if (decision === "approved") {
                activeStream.current = false;
                await startStream();
            }
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Decision failed.");
        } finally { setDecisionBusy(false); }
    };

    const cancel = async () => {
        try {
            await api.post(`/orchestration-runs/${runId}/cancel`);
            toast.success("Run cancelled.");
            fetchOnce();
        } catch { toast.error("Cancel failed."); }
    };

    if (loading || !run) {
        return (
            <div className="h-full grid place-items-center text-[13px]"
                style={{ color: "var(--atlas-text-muted)" }}>
                Loading run…
            </div>
        );
    }

    const runStyle = STATUS_STYLE[run.status] || STATUS_STYLE.pending;
    const nodeById = Object.fromEntries((orch?.nodes || []).map((n) => [n.id, n]));

    return (
        <div className="h-full overflow-y-auto atlas-scrollbar" data-testid="orch-run-detail">
            <div className="max-w-6xl mx-auto px-8 py-8">
                {/* Header */}
                <Link to={`/app/orchestrations/${orchId}/runs`}
                    className="inline-flex items-center gap-1 text-[12px]"
                    style={{ color: "var(--atlas-text-tertiary)" }}
                    data-testid="orch-run-back">
                    <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Runs
                </Link>
                <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
                    <div className="min-w-0">
                        <div className="atlas-hero-eyebrow">
                            <Clock className="w-3 h-3" strokeWidth={2.5} /> Run detail
                        </div>
                        <h1 className="atlas-text-gradient mt-3 text-[28px] font-semibold tracking-tight">
                            {orch?.name || "Run"}
                        </h1>
                        <div className="mt-1 text-[11.5px] font-mono"
                            style={{ color: "var(--atlas-text-muted)" }}>
                            {run.id}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full"
                            style={{
                                background: runStyle.bg,
                                border: `1px solid ${runStyle.border}`,
                                color: runStyle.color,
                            }}
                            data-testid="orch-run-status-pill">
                            {run.status?.toUpperCase().replace(/_/g, " ")}
                        </span>
                        {!["success", "failed", "cancelled"].includes(run.status) && (
                            <button onClick={cancel}
                                className="atlas-btn-ghost inline-flex items-center rounded-lg px-3 h-9 text-[12.5px]"
                                data-testid="orch-run-cancel">
                                Cancel run
                            </button>
                        )}
                    </div>
                </div>

                {/* Awaiting approval banner */}
                {run.status === "awaiting_approval" && run.awaiting_approval_for && (
                    <div className="mt-6 atlas-card p-5"
                        style={{
                            background: "linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(10,10,26,0.85) 100%)",
                            border: "1px solid rgba(245,158,11,0.55)",
                        }}
                        data-testid="orch-run-approval-panel">
                        <div className="flex items-start gap-3">
                            <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5"
                                strokeWidth={2} style={{ color: "#fbbf24" }} />
                            <div className="min-w-0 flex-1">
                                <div className="text-[14px] font-semibold" style={{ color: "#fbbf24" }}>
                                    Approval required
                                </div>
                                <p className="mt-1 text-[12.5px]"
                                    style={{ color: "var(--atlas-text-secondary)" }}>
                                    {(() => {
                                        const gate = (run.node_runs || []).find((nr) => nr.node_id === run.awaiting_approval_for);
                                        const message = gate?.approval?.message
                                            || nodeById[run.awaiting_approval_for]?.config?.message
                                            || "This run is paused pending approval.";
                                        return message;
                                    })()}
                                </p>
                                <textarea value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    rows={2}
                                    placeholder="Optional reason (required if rejecting)…"
                                    className="mt-3 w-full rounded-md p-2 text-[12.5px]"
                                    data-testid="orch-approve-reason" />
                                <div className="mt-3 flex items-center gap-2">
                                    <button onClick={() => approveGate("approved")}
                                        disabled={decisionBusy}
                                        className="atlas-btn-primary inline-flex items-center gap-1 rounded-lg px-4 h-9 text-[12.5px]"
                                        data-testid="orch-approve-btn">
                                        {decisionBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2}/> : <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2}/>}
                                        Approve
                                    </button>
                                    <button onClick={() => approveGate("rejected")}
                                        disabled={decisionBusy}
                                        className="inline-flex items-center gap-1 rounded-lg px-4 h-9 text-[12.5px] font-medium"
                                        style={{
                                            background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                                            color: "#fff",
                                            boxShadow: "0 0 0 1px rgba(239,68,68,0.55), 0 6px 18px -6px rgba(220,38,38,0.55)",
                                        }}
                                        data-testid="orch-reject-btn">
                                        <XCircle className="w-3.5 h-3.5" strokeWidth={2}/>
                                        Reject
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Failure banner */}
                {run.status === "failed" && run.failure_reason && (
                    <div className="mt-6 rounded-lg p-3 text-[13px]"
                        style={{
                            background: "var(--atlas-danger-muted)",
                            border: "1px solid rgba(239,68,68,0.45)",
                            color: "#fca5a5",
                        }}
                        data-testid="orch-run-failure-reason">
                        <AlertTriangle className="w-4 h-4 inline mr-1 -mt-1" strokeWidth={2}/>
                        {run.failure_reason}
                    </div>
                )}

                {/* Two-column body */}
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Node status list */}
                    <div className="lg:col-span-1 space-y-2">
                        <div className="atlas-label">Node execution</div>
                        {(orch?.nodes || []).map((n) => {
                            const nr = (run.node_runs || []).find((x) => x.node_id === n.id);
                            const meta = NODE_META[n.type] || NODE_META.trigger;
                            const st = STATUS_STYLE[nr?.status] || STATUS_STYLE.pending;
                            const NodeIcon = meta.icon;
                            return (
                                <div key={n.id}
                                    className="atlas-card p-3"
                                    style={{ borderColor: nr ? st.border : "var(--atlas-border)" }}
                                    data-testid={`orch-run-node-${n.id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <NodeIcon className="w-3.5 h-3.5 flex-shrink-0"
                                                strokeWidth={2.25} style={{ color: meta.color }} />
                                            <div className="text-[12.5px] font-medium truncate"
                                                style={{ color: "var(--atlas-text-primary)" }}>
                                                {n.config?.label || n.id}
                                            </div>
                                        </div>
                                        <span className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded-full flex-shrink-0"
                                            style={{
                                                background: st.bg,
                                                border: `1px solid ${st.border}`,
                                                color: st.color,
                                            }}>
                                            {nr ? nr.status.toUpperCase().replace(/_/g, " ") : "PENDING"}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Timeline */}
                    <div className="lg:col-span-2 space-y-3">
                        <div className="atlas-label">Timeline</div>
                        {(run.node_runs || []).length === 0 ? (
                            <div className="text-[13px]" style={{ color: "var(--atlas-text-muted)" }}>
                                No node executions yet.
                            </div>
                        ) : (
                            (run.node_runs || []).map((nr, i) => (
                                <TimelineRow key={`${nr.node_id}-${i}`} nr={nr} nodeDef={nodeById[nr.node_id]} />
                            ))
                        )}
                    </div>
                </div>
                <div className="h-16" />
            </div>
        </div>
    );
}

function TimelineRow({ nr, nodeDef }) {
    const meta = NODE_META[nr.node_type] || NODE_META.trigger;
    const st = STATUS_STYLE[nr.status] || STATUS_STYLE.pending;
    const Icon = meta.icon;
    const outText = nr.output?.text || (nr.output ? JSON.stringify(nr.output, null, 2) : "");
    return (
        <div className="atlas-card atlas-card--elevated p-4"
            style={{ borderColor: st.border }}
            data-testid={`orch-run-timeline-${nr.node_id}`}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4" strokeWidth={2.25} style={{ color: meta.color }} />
                    <div className="min-w-0">
                        <div className="text-[13px] font-semibold truncate"
                            style={{ color: "var(--atlas-text-primary)" }}>
                            {nodeDef?.config?.label || nr.label || nr.node_id}
                        </div>
                        <div className="text-[10px] font-mono"
                            style={{ color: "var(--atlas-text-muted)" }}>
                            {nr.started_at?.slice(11, 19)}{nr.ended_at ? ` → ${nr.ended_at.slice(11, 19)}` : ""}
                        </div>
                    </div>
                </div>
                <span className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded-full"
                    style={{ background: st.bg, border: `1px solid ${st.border}`, color: st.color }}>
                    {nr.status.toUpperCase().replace(/_/g, " ")}
                </span>
            </div>
            {nr.error && (
                <div className="mt-2 text-[12px]" style={{ color: "#fca5a5" }}>
                    ⚠ {nr.error}
                </div>
            )}
            {nr.input && Object.keys(nr.input).length > 0 && (
                <details className="mt-3">
                    <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-widest"
                        style={{ color: "var(--atlas-text-muted)" }}>
                        Input
                    </summary>
                    <pre className="mt-2 rounded-md p-2 text-[11.5px] font-mono overflow-x-auto atlas-scrollbar"
                        style={{
                            background: "var(--atlas-bg-deep)",
                            border: "1px solid var(--atlas-border-strong)",
                            color: "var(--atlas-text-secondary)",
                        }}>
{JSON.stringify(nr.input, null, 2)}
                    </pre>
                </details>
            )}
            {outText && (
                <div className="mt-3">
                    <div className="text-[11px] font-semibold uppercase tracking-widest mb-1"
                        style={{ color: "var(--atlas-text-muted)" }}>
                        Output
                    </div>
                    <div className="whitespace-pre-wrap text-[12.5px] leading-relaxed rounded-md p-3 max-h-[300px] overflow-y-auto atlas-scrollbar"
                        style={{
                            background: "var(--atlas-bg-deep)",
                            border: "1px solid var(--atlas-border-strong)",
                            color: "var(--atlas-text-primary)",
                        }}>
                        {outText}
                    </div>
                </div>
            )}
            {nr.approval && (
                <div className="mt-3 rounded-md p-2 text-[11.5px]"
                    style={{
                        background: "rgba(245,158,11,0.10)",
                        border: "1px solid rgba(245,158,11,0.45)",
                        color: "#fbbf24",
                    }}>
                    <b>Approval:</b> {nr.approval.decision || "pending"} · approver_role={nr.approval.approver_role}
                    {nr.approval.reason && <div className="mt-1"><i>Reason:</i> {nr.approval.reason}</div>}
                </div>
            )}
        </div>
    );
}
